import { FavoriteProposalType, FavoriteModeType, FavoriteEtabType } from "types";
import { getLocalStorageItem, setLocalStorageItem, removeLocalStorageItem } from "./localStorage";


/**
 * If the value of the environment variable REACT_APP_FAVORITES_ETAB is true, then return true,
 * otherwise return false.
 * @returns A function that returns a boolean.
 */
export const showFavoritesEtab = () => {
    return process.env.REACT_APP_FAVORITES_ETAB === JSON.stringify(true);
}

/**
 * If the environment variable REACT_APP_FAVORITES_PROPOSAL is set to true, then return true, otherwise
 * return false.
 * @returns A function that returns a boolean.
 */
export const showFavoritesProposal = () => {
    return process.env.REACT_APP_FAVORITES_PROPOSAL === JSON.stringify(true);
}

/**
 * It returns an array of FavoriteProposalType objects that have a session property that matches the session
 * parameter
 * @param {string} session - string - the session id
 * @returns An array of FavoriteProposalType objects.
 */
export const getFavoritesProposals = (session: string): FavoriteProposalType[] => {
    const keys = JSON.parse(getLocalStorageItem(getStorageKeyByMode("proposal")) || '[]') as FavoriteProposalType[];
    return keys.filter(key => key.session === session);
}


/**
 * It returns an array of FavoriteEtabType objects that have a session property that matches the session
 * parameter
 * @param {string} session - string - the session id
 * @returns An array of FavoriteEtabType objects.
 */
export const getFavoritesEtabs = (session: string): FavoriteEtabType[] => {
    const keys = JSON.parse(getLocalStorageItem(getStorageKeyByMode("etab")) || '[]') as FavoriteEtabType[];
    return keys.filter(key => key.session === session);
}

/**
 * It returns the favorite object from the favorites array that has a proposalKey property that matches
 * the proposalKey argument
 * @param {string} session - string - the session key
 * @param {string} proposalKey - string
 * @returns The return type is FavoriteProposalType | null.
 */
export const getFavoriteProposal = (session: string, proposalKey: string): FavoriteProposalType | null => {
    const favorites = getFavoritesProposals(session);
    return favorites.find(favorite => favorite.proposalKey === proposalKey) ?? null;
}

/**
 * It returns the favorite object from the favorites array that has a etabId property that matches
 * the etabId argument
 * @param {string} session - string - the session key
 * @param {string} etabId - string
 * @returns The return type is FavoriteEtabType | null.
 */
export const getFavoriteEtab = (session: string, etabId: number): FavoriteEtabType | null => {
    const favorites = getFavoritesEtabs(session);
    return favorites.find(favorite => favorite.etabId === etabId) ?? null;
}

/**
 * If the proposalKey is not already in the favorites, add it to the favorites, otherwise remove it
 * from the favorites.
 * @param {string} session - string
 * @param {string} proposalKey - string
 */
export const addRemoveFavoriteProposal = (session: string, proposalKey: string, callback: (favorites: FavoriteProposalType[]) => void) => {
    let keys = JSON.parse(getLocalStorageItem(getStorageKeyByMode("proposal")) || '[]') as FavoriteProposalType[];
    let result: FavoriteProposalType[] = []
    if (!keys.find(k => k.proposalKey === proposalKey)) {
        const key: FavoriteProposalType = {
            session,
            proposalKey
        };
        result = [...keys, key];
        setLocalStorageItem(getStorageKeyByMode("proposal"), JSON.stringify(result));
    } else {
        keys = keys.filter(k => k.proposalKey !== proposalKey);
        result = [...keys];
        setLocalStorageItem(getStorageKeyByMode("proposal"), JSON.stringify(result));
    }
    callback(result);
}

/**
 * If the etabId is not already in the favorites, add it to the favorites, otherwise remove it
 * from the favorites.
 * @param {string} session - string
 * @param {string} etabId - string
 */
export const addRemoveFavoriteEtab = (session: string, etabId: number, callback: (favorites: FavoriteEtabType[]) => void) => {
    let keys = JSON.parse(getLocalStorageItem(getStorageKeyByMode("etab")) || '[]') as FavoriteEtabType[];
    let result: FavoriteEtabType[] = []
    if (!keys.find(k => k.etabId === etabId)) {
        const key: FavoriteEtabType = {
            session,
            etabId
        };
        result = [...keys, key];
        setLocalStorageItem(getStorageKeyByMode("etab"), JSON.stringify(result));
    } else {
        keys = keys.filter(k => k.etabId !== etabId);
        result = [...keys];
        setLocalStorageItem(getStorageKeyByMode("etab"), JSON.stringify(result));
    }
    callback(result);
}

/**
 * It takes a session and a callback function as parameters, and returns the callback function with the
 * result of the filter operation.
 * @param {string} session - string
 * @param callback - (favorites: FavoriteProposalType[]) => void
 */
export const clearFavoritesProposals = (session: string, callback: (favorites: FavoriteProposalType[]) => void) => {
    const favorites = getFavoritesProposals(session);
    const result = favorites.filter(x => x.session !== session);
    if (result && result.length > 0) {
        setLocalStorageItem(getStorageKeyByMode("proposal"), JSON.stringify(result));
    } else {
        removeLocalStorageItem(getStorageKeyByMode("proposal"));
    }
    callback(result);
}

/**
 * It takes a session and a callback function as parameters, and returns the callback function with the
 * result of the filter operation.
 * @param {string} session - string
 * @param callback - (favorites: FavoriteEtabType[]) => void
 */
export const clearFavoritesEtabs = (session: string, callback: (favorites: FavoriteEtabType[]) => void) => {
    const favorites = getFavoritesEtabs(session);
    const result = favorites.filter(x => x.session !== session);
    if (result && result.length > 0) {
        setLocalStorageItem(getStorageKeyByMode("etab"), JSON.stringify(result));
    } else {
        removeLocalStorageItem(getStorageKeyByMode("etab"));
    }
    callback(result);
}

/**
 * If the mode is proposal, return rsl_favoritesProposal, otherwise return rsl_favoritesEtab
 * @param {FavoriteModeType} mode - FavoriteModeType
 * @returns A function that takes a parameter and returns a string.
 */
export const getStorageKeyByMode = (mode: FavoriteModeType) => {
    return mode === "proposal" ? "rsl_favoritesProposal" : "rsl_favoritesEtab";
}
